import { IconFileImport } from "@/assets/icons/IconFileImport";
import { CardContainer } from "@/components/commons/CardContainer";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { FeaturePagination } from "@/features/learning-content/components/FeaturePagination";
import { TWordData } from "@/features/learning-content/types/learningContent";
import { dayjs } from "@/utils/dayjs";
import { useTable } from "@/hooks/useTable";
import { ExplanationLanguageCountCell } from "@/features/learning-content/components/ExplanationLanguageCountCell";
import { ImagePreviewCell } from "@/features/learning-content/components/ImagePreviewCell";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Link, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";

const COMMON_CATEGORY_NAME = "共通";

// Optional column, shown only when the list endpoint returns vocabulary images
const IMAGE_COLUMN: MRT_ColumnDef<TWordData> = {
  header: "画像",
  accessorKey: "imageUrl",
  size: 80,
  muiTableHeadCellProps: { align: "center", sx: { width: "80px", ".Mui-TableHeadCell-Content": { justifyContent: "center" } } },
  muiTableBodyCellProps: { align: "center", sx: { width: "80px" } },
  Cell: ({ row }) => <ImagePreviewCell filePath={row.original.imageUrl} alt="単語画像" />,
};

type TWordDataTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  data: TWordData[];
  totalPage: number;
  totalCount: number;
  page: number;
  categoryCount?: number;
  showCategoryColumn?: boolean;
  showImageColumn?: boolean;
  onPageChange: (page: number) => void;
  onOpenSheet?: () => void;
  onSync?: () => void;
  onAdd?: () => void;
  onExport?: () => void;
};

// Table component for displaying word data with pagination
export const WordDataTable: FC<TWordDataTableProps> = ({
  isLoading,
  isFetching,
  data,
  totalPage,
  totalCount,
  page,
  categoryCount = 0,
  showCategoryColumn = false,
  showImageColumn = false,
  onPageChange,
  onOpenSheet,
  onSync,
}) => {
  const columns = useMemo<MRT_ColumnDef<TWordData>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        muiTableHeadCellProps: { sx: { width: "60px", minWidth: "60px" } },
        muiTableBodyCellProps: { sx: { width: "60px", minWidth: "60px" } },
        Cell: ({ row }) => row.original.wordId,
      },
      ...(showCategoryColumn
        ? [
            {
              header: "カテゴリー",
              accessorKey: "category",
              size: 150,
              muiTableHeadCellProps: { sx: { width: "150px" } },
              muiTableBodyCellProps: { sx: { width: "150px" } },
              Cell: ({ row }: { row: { original: TWordData } }) => {
                const { category } = row.original;
                if (categoryCount <= 1) {
                  return category || COMMON_CATEGORY_NAME;
                }
                return category || EMPTY_STR.TEXT;
              },
            },
          ]
        : []),
      {
        header: "単語",
        accessorKey: "wordName",
        size: 150,
        muiTableHeadCellProps: { sx: { width: "150px" } },
        muiTableBodyCellProps: { sx: { width: "150px" } },
        Cell: ({ row }) => row.original.wordName || EMPTY_STR.TEXT,
      },
      {
        header: "ふりがな",
        accessorKey: "reading",
        size: 120,
        muiTableHeadCellProps: { sx: { width: "120px" } },
        muiTableBodyCellProps: { sx: { width: "120px" } },
        Cell: ({ row }) => row.original.reading || EMPTY_STR.TEXT,
      },
      ...(showImageColumn ? [IMAGE_COLUMN] : []),
      {
        header: "解説言語数",
        accessorKey: "supportedLanguages",
        size: 100,
        muiTableHeadCellProps: { sx: { width: "100px" } },
        muiTableBodyCellProps: { sx: { width: "100px" } },
        Cell: ({ row }) => <ExplanationLanguageCountCell supportedLanguages={row.original.supportedLanguages ?? []} />,
      },
      {
        header: "更新日",
        accessorKey: "updatedDate",
        size: 106,
        muiTableHeadCellProps: { sx: { width: "106px" } },
        muiTableBodyCellProps: { sx: { width: "106px" } },
        Cell: ({ row }) => {
          const date = row.original.updatedDate;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
    ],
    [categoryCount, showCategoryColumn, showImageColumn],
  );

  const { table } = useTable({
    columns,
    rows: data ?? [],
    enableSorting: false,
    enableRowActions: false,
  });

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        {/* Header with title and action buttons */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="sub16Bold">単語データ ({totalCount})</Typography>
          <Stack direction="row" gap={2} alignItems="center">
            {onOpenSheet && (
              <Link
                component="button"
                onClick={onOpenSheet}
                underline="always"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, color: "#185DE3" }}
              >
                シートを開く
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            )}
            {onSync && (
              <Button size="sm" variant="outline" onClick={onSync} startIcon={<IconFileImport />}>
                シートから反映
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Info text */}
        <Typography variant="body14" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
          規定の仕様に基づくExcelファイルをインポートして、単語の追加または更新をしてください。
          <br />
          削除する単語のIDは廃番として、行ごと削除するかIDを空欄にしてください。IDのない単語は無効化されます。
        </Typography>

        {/* Table */}
        <Table table={table} isLoading={isLoading} isFetching={isFetching} />

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <FeaturePagination page={page} totalPage={totalPage} onChange={onPageChange} />
        </Box>
      </Stack>
    </CardContainer>
  );
};
